<template>
<div class="hair-page">
  <div class="hw-card">
    <h1 class="hw-title">Vos cheveux</h1>
    <p class="hw-subtitle">Aidez-nous à estimer la durée de votre rendez-vous</p>

    <div class="section">
      <label class="section-label">Longueur</label>
      <div class="option-grid">
        <button
          v-for="opt in lengthOptions"
          :key="opt.value"
          type="button"
          class="option-card"
          :class="{ selected: length && length.value === opt.value }"
          @click="selectLength(opt)"
        >
          <span class="option-name">{{ opt.name }}</span>
        </button>
      </div>
      <small v-show="validationErrors.length && submitted" class="form-error">
        Merci d'indiquer une longueur
      </small>
      <div v-if="lengthInfo" class="length-hint">
        <i class="pi pi-info-circle"></i>
        {{ lengthInfo }}
      </div>
    </div>

    <div class="section">
      <label class="section-label">Épaisseur</label>
      <div class="option-grid thickness-grid">
        <button
          v-for="opt in thicknessOptions"
          :key="opt.value"
          type="button"
          class="option-card"
          :class="{ selected: thickness && thickness.value === opt.value }"
          @click="thickness = opt"
        >
          <span class="option-name">{{ opt.name }}</span>
        </button>
      </div>
      <small v-show="validationErrors.thickness && submitted" class="form-error">
        Merci d'indiquer une épaisseur
      </small>
    </div>
  </div>

  <div class="hw-actions">
    <button class="hw-btn hw-btn-secondary" @click="previousPage()">
      <i class="pi pi-arrow-left"></i>
      Retour
    </button>
    <button class="hw-btn hw-btn-primary" @click="nextPage()">
      Suivant
      <i class="pi pi-arrow-right"></i>
    </button>
  </div>
</div>
</template>

<style scoped>
.hair-page {
  text-align: left;
}

.section {
  margin-bottom: 1.5rem;
}

.section:last-of-type {
  margin-bottom: 0;
}

.section-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 700;
  color: #374151;
  margin-bottom: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.option-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 0.5rem;
}

.thickness-grid {
  grid-template-columns: repeat(2, 1fr);
}

.option-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  padding: 0.75rem 0.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.option-card:hover {
  border-color: #93c5fd;
  background: #eff6ff;
}

.option-card.selected {
  border-color: #3b82f6;
  background: #eff6ff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.option-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1e293b;
  text-align: center;
}

.option-card.selected .option-name {
  color: #1d4ed8;
}

.length-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.6rem;
  padding: 0.6rem 0.8rem;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 10px;
  font-size: 0.85rem;
  color: #0369a1;
}

.length-hint .pi {
  flex-shrink: 0;
}

.form-error {
  display: block;
  color: #dc2626;
  font-size: 0.8rem;
  margin-top: 0.4rem;
}

@media (max-width: 400px) {
  .option-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .thickness-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>

<script setup>
import { defineEmits, ref, onMounted } from 'vue'
import { useMainStore } from '../store'

const store = useMainStore();

const thickness = ref(null);
const length = ref(null);

const validationErrors = ref({});

onMounted(() => {
  if (!store.clientData.firstname) {
    emit('go-home');
  }
});

const emit = defineEmits(['next-page', 'previous-page', 'go-home']);

function nextPage() {
  submitted.value = true;
  if (validateForm()) {
    saveValues();
    emit('next-page', { pageIndex: 1 });
  }
}

function previousPage() {
  emit('previous-page', { pageIndex: 1 });
}

const lengthInfo = ref("");

var submitted = ref(false);

const lengthOptions = ref([
  { name: 'Très courts', value: 'xs' },
  { name: 'Courts', value: 's' },
  { name: 'Mi-longs', value: 'm' },
  { name: 'Longs', value: 'l' },
  { name: 'Très longs', value: 'xl' },
]);

const thicknessOptions = ref([
  { name: 'Fins', value: 'fin' },
  { name: 'Moyen', value: 'moyen' },
  { name: 'Épais', value: 'epais' },
  { name: 'Je ne sais pas', value: 'nsp' },
]);

function selectLength(opt) {
  length.value = opt;
  changeLength();
}

function changeLength() {
  switch (length.value?.value) {
    case 'xs':
      lengthInfo.value = "J'ai le crâne rasé, ou presque !";
      break;
    case 's':
      lengthInfo.value = "Mes cheveux ne dépassent pas le menton";
      break;
    case 'm':
      lengthInfo.value = "Mes cheveux ne dépassent pas les épaules";
      break;
    case 'l':
      lengthInfo.value = "Mes cheveux ne dépassent pas le milieu du dos";
      break;
    case 'xl':
      lengthInfo.value = "Mes cheveux dépassent le milieu du dos !";
      break;
    default:
      lengthInfo.value = "";
      break;
  }
}

function saveValues() {
  store.$patch((state) => {
    state.length = length.value.value;
    state.thickness = thickness.value.value;
  });
}

function validateForm() {
  if (!thickness.value)
    validationErrors.value['thickness'] = true;
  else
    delete validationErrors.value['thickness'];

  if (!length.value)
    validationErrors.value['length'] = true;
  else
    delete validationErrors.value['length'];

  return !Object.keys(validationErrors.value).length;
}
</script>
